import { z } from 'zod';
import { NonEmptyStringSchema } from './primitives';
import { SLOT_MODEL_AUTHORABLE, SlotKindSchema } from './statement';

/**
 * What the LLM contextualiser is allowed to return (docs/DECISIONS.md ZD-063).
 *
 * ## The whole output is sentences, and that is the point
 *
 * There is no field here for an object id, a citation, a basis, a confidence, a
 * severity, a slot state or a coverage verdict. `IDENTITY_WORKER.md` §5's rule
 * applied one worker along: **a prohibition expressed as a missing field is the
 * only kind a model cannot argue with**, and everything this worker must not
 * assert is absent rather than forbidden.
 *
 * What that buys is the containment argument, which is structural rather than
 * asserted: the contextualiser writes into `Slot.authored_text`, and every
 * downstream consumer — the reproducibility tuple, scoring, `evidence_refs`,
 * the clarification set, the control mappings — reads the transcript beside it.
 * A wrong sentence is a wrong sentence and cannot become a wrong fact.
 *
 * ## An empty answer is a real answer, and the schema had to be able to say so
 *
 * `slots` has **no `.min(1)`**, deliberately. ZD-019's question run on an output
 * schema: *what does a worker write when it has nothing to add?* A required
 * non-empty list would manufacture a contextualisation for every finding it was
 * shown, which is the same defect as a required field with no honest value —
 * and here it would arrive as prose a client reads.
 *
 * On the corpus that is not a marginal case. `grounds` is the only slot a model
 * may write and it is `not_applicable` on **62 of 102** findings, because an
 * `unknown` verdict carries no witnesses by construction. So the honest answer
 * on the majority of this corpus's findings is *nothing*, and it has to be
 * expressible or the majority becomes invention.
 */

/**
 * The most a model may write into one slot.
 *
 * A ceiling rather than a target, and derived rather than picked: the longest
 * rationale the deterministic renderer produces on the corpus is **896**
 * characters and the longest text the product emits anywhere is the catalogue's
 * at-scale implementation tier at **505** (ZD-060 §5). 1200 leaves headroom over
 * the longest thing the pipeline already writes without admitting an essay into
 * a slot whose rendered form is a paragraph.
 *
 * It is also a format constraint, not only a taste one. ZD-057 §4's limit —
 * verbatim is a string property and a report is a format — cuts both ways: text
 * with no bound is text a width-constrained cell will truncate, and a truncated
 * sentence is a softened one.
 */
export const SLOT_TEXT_MAX = 1200;

/**
 * One slot's sentence.
 *
 * **`SLOT_MODEL_AUTHORABLE` is read here and copied nowhere.** The reviewer's
 * equivalent (`SlotEditSchema`) reads `SLOT_REVIEWER_EDITABLE`, and the two
 * schemas exist because the two tables now differ — while they agreed, two
 * schemas would have been two copies of one rule and the shape ZD-048 §7 warns
 * about, where breaking either changes nothing.
 */
export const AuthoredSlotSchema = z
  .object({
    slot: SlotKindSchema,
    /** Lands in `Slot.authored_text`, beside the transcript, never over it. */
    text: NonEmptyStringSchema.max(SLOT_TEXT_MAX),
  })
  .superRefine((answer, ctx) => {
    if (!SLOT_MODEL_AUTHORABLE[answer.slot]) {
      ctx.addIssue({
        code: 'custom',
        message:
          `slot ${answer.slot} may not be written by a model: it carries either a fact about ` +
          `the client's architecture, text that must survive verbatim, or pack text a model may ` +
          `not rewrite`,
        path: ['slot'],
      });
    }
  });
export type AuthoredSlot = z.infer<typeof AuthoredSlotSchema>;

export const ContextualisationProposalSchema = z
  .object({
    slots: z.array(AuthoredSlotSchema),
  })
  .superRefine((proposal, ctx) => {
    const seen = new Set<string>();
    proposal.slots.forEach((answer, i) => {
      if (seen.has(answer.slot)) {
        ctx.addIssue({
          code: 'custom',
          message:
            `slot ${answer.slot} is answered twice: a statement carries one sentence per slot, ` +
            `so a second answer is a choice nothing downstream could make`,
          path: ['slots', i, 'slot'],
        });
      }
      seen.add(answer.slot);
    });
  });
export type ContextualisationProposal = z.infer<typeof ContextualisationProposalSchema>;
